/**
 * One vulnerability case on the shared time axis of the page „Meldefristen“:
 * the stretches (initial assessment, reporting phase, final report phase),
 * the deadlines coloured by their state and the milestones as markers.
 *
 * The axis is decoration for sighted users (`aria-hidden`); the label column
 * carries the same standing as text.
 */
import { Trans, useLingui } from '@lingui/react/macro';
import type { CSSProperties, ReactNode } from 'react';

import Chip from '../../components/Chip';
import { backendLabel } from '../../lib/backendLabels';
import { fmtDate } from '../../lib/format';
import type { CaseClock, TimelineCase, TimelineMilestone } from '../../lib/types';
import { CASE_STATUS, clockChip } from '../triage/CasePanel';

import CaseTimelineDetail from './CaseTimelineDetail';
import { milestoneLabel, position, span, standing, type TimeWindow } from './timelineModel';

type MarkerTone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info';

function clockTone(clock: CaseClock): MarkerTone {
  switch (clock.state) {
    case 'submitted':
      return clock.on_time === false ? 'warn' : 'ok';
    case 'overdue':
      return 'danger';
    case 'due_soon':
      return 'warn';
    default:
      return 'neutral';
  }
}

const EVENT_TONE: Readonly<Record<string, MarkerTone>> = { OVERDUE: 'danger', DUE_SOON: 'warn', STARTED: 'neutral' };

function milestoneTone(milestone: TimelineMilestone): MarkerTone {
  if (milestone.kind === 'report') return milestone.on_time === false ? 'warn' : 'ok';
  if (milestone.kind === 'event') return EVENT_TONE[milestone.event_type ?? ''] ?? 'neutral';
  if (milestone.kind === 'corrective') return 'ok';
  return 'info';
}

/** Start and end of a stretch are drawn by the stretches themselves. */
const DRAWN_AS_STRETCH = new Set(['hit', 'aware']);

function at(left: number): CSSProperties {
  return { left: `${left}%` };
}

function Stretch({ from, to, axis, className, title }: Readonly<{
  from: string | null; to: string | null; axis: TimeWindow; className: string; title: string;
}>): ReactNode {
  const box = span(from, to, axis);
  return box ? <span className={`tl-seg ${className}`} style={{ left: `${box.left}%`, width: `${box.width}%` }} title={title} /> : null;
}

function Track({ row, axis, today }: Readonly<{ row: TimelineCase; axis: TimeWindow; today: number | null }>): ReactNode {
  const { t } = useLingui();
  const due72 = row.clocks.find((clock) => clock.key === '72h')?.due ?? null;
  const due14 = row.clocks.find((clock) => clock.key === '14d')?.due ?? null;
  return (
    <span className="tl-track" aria-hidden="true">
      <Stretch from={row.first_hit_at} to={row.aware_since} axis={axis} className="tl-seg-triage"
        title={t`Erstprüfung: Frist läuft noch nicht`} />
      <Stretch from={row.aware_since} to={due72} axis={axis} className="tl-seg-report"
        title={t`Meldephase: Frühwarnung und Meldung`} />
      <Stretch from={row.corrective_measure.available_at} to={due14} axis={axis} className="tl-seg-final"
        title={t`Abschlussbericht`} />
      {today !== null ? <span className="tl-today" style={at(today)} /> : null}
      {row.clocks.map((clock) => {
        const left = position(clock.due, axis);
        if (left === null) return null;
        const label = backendLabel(clock.label);
        const due = fmtDate(clock.due);
        const state = clockChip(clock).label;
        return (
          <span key={clock.key} className={`tl-due tl-${clockTone(clock)}`} style={at(left)}
            title={t`${label}: fällig ${due} · ${state}`} />
        );
      })}
      {row.milestones.filter((milestone) => !DRAWN_AS_STRETCH.has(milestone.kind)).map((milestone, index) => {
        const left = position(milestone.at, axis);
        return left === null ? null : (
          <span key={`${milestone.kind}-${index}`} className={`tl-mark tl-mark-${milestone.kind} tl-${milestoneTone(milestone)}`}
            style={at(left)} title={`${milestoneLabel(milestone)} · ${fmtDate(milestone.at)}`} />
        );
      })}
    </span>
  );
}

export interface TimelineRowProps {
  row: TimelineCase;
  axis: TimeWindow;
  today: number | null;
  open: boolean;
  onToggle: () => void;
}

export default function TimelineRow({ row, axis, today, open, onToggle }: Readonly<TimelineRowProps>): ReactNode {
  const { t } = useLingui();
  const productTypeId = row.product_type_id;
  const status = CASE_STATUS[row.status];
  const detailId = `meldefristen-case-${row.id}`;
  return (
    <li className={`tl-row${open ? ' is-open' : ''}`} id={`meldefristen-row-${row.id}`}>
      <button type="button" className="tl-row-head" aria-expanded={open} aria-controls={detailId} onClick={onToggle}>
        <span className="tl-label">
          <span className="tl-title">
            <strong>{row.cve}</strong>
            <span className="muted">· {row.product_name ?? t`Produkt #${productTypeId}`}</span>
            {row.status === 'OPEN' ? null : <Chip tone={status.tone}>{status.label}</Chip>}
            {row.contradiction ? <Chip tone="alert"><Trans>Widerspruch</Trans></Chip> : null}
          </span>
          <span className="tl-standing">{standing(row)}</span>
        </span>
        <Track row={row} axis={axis} today={today} />
      </button>
      {open ? <CaseTimelineDetail id={detailId} row={row} /> : null}
    </li>
  );
}
